import { useMemo } from 'react';

import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';

/**
 * Page-level access gate for the training admin pages (ADR-025).
 * Mirrors the server gates exactly — `manage_training` for authoring
 * surfaces, `view_training_reports` for reporting surfaces; superadmins
 * (visionary/integrator) pass implicitly (ADR-017). Client-side gating
 * is VISIBILITY only; the server re-enforces on every request.
 *
 * Lives in its own file so page modules export components only
 * (Vite's react-refresh rule) — shipedge `_gate.ts` convention.
 */
export function useTrainingAdminGate(): { canManage: boolean; canViewReports: boolean } {
  const { state } = useAuth();
  return useMemo(() => {
    if (state.status !== 'authenticated') return { canManage: false, canViewReports: false };
    const me = state.me;
    return {
      canManage: isSuperadmin(me) || hasCapability(me, 'manage_training'),
      canViewReports: isSuperadmin(me) || hasCapability(me, 'view_training_reports'),
    };
  }, [state]);
}
